import { Link } from 'react-router-dom'
import type { ColDef } from 'ag-grid-community'
import {
  ItemStatusBadge,
  PriorityBadge,
  CategoryBadge,
  itemStatusLabel,
  ProcItemStatusBadge,
} from '../../shared/ui/Badge'
import type { RequestItemStatus } from '../../shared/api/types'
import type { QueueRow } from './queueTypes'
import {
  ITEM_TRANSITIONS,
  PROCUREMENT_STATE_LABELS,
  PROCUREMENT_STATE_MANUAL,
  type RequestItemProcurementState,
} from '@crm/shared'

/**
 * Быстрые переходы статуса в очереди (ролям без закупщицкого state — в очереди
 * это только ГД: у ADMIN она закрыта с 18.09.2026) — подмножество строгой
 * карты ITEM_TRANSITIONS (@crm/shared). Сервер с 14.09.2026 отклоняет переходы
 * вне карты, поэтому варианты дополнительно фильтруются по карте
 * (`quickTransitionsFor`), а тест queueQuickTransitions ловит расхождение.
 */
export const QUICK_TRANSITIONS: Partial<Record<RequestItemStatus, RequestItemStatus[]>> = {
  NEW: ['IN_QUEUE'],
  IN_QUEUE: ['NO_RESPONSE'],
  IN_PROGRESS: ['NO_RESPONSE'],
  QUOTED: ['READY_FOR_QUOTATION', 'IN_PROGRESS'],
  PARTIAL: ['READY_FOR_QUOTATION', 'IN_PROGRESS'],
  REWORK: ['IN_PROGRESS'],
  NEEDS_INFO: ['IN_PROGRESS'],
  NO_RESPONSE: ['IN_QUEUE'],
}

/** Быстрые переходы из статуса, разрешённые строгой картой. */
export function quickTransitionsFor(status: RequestItemStatus): RequestItemStatus[] {
  const allowed = ITEM_TRANSITIONS[status] ?? []
  return (QUICK_TRANSITIONS[status] ?? []).filter((s) => allowed.includes(s))
}

function deadlineClass(deadline: string | null, isOverdue: boolean): string {
  if (!deadline) return ''
  if (isOverdue) return 'text-red-600 font-semibold'
  const days = (new Date(deadline).getTime() - Date.now()) / 86_400_000
  if (days <= 3) return 'text-amber-600 font-medium'
  return 'text-gray-500'
}

function deadlineDot(deadline: string | null, isOverdue: boolean): string {
  if (!deadline) return ''
  if (isOverdue) return 'bg-red-500'
  const days = (new Date(deadline).getTime() - Date.now()) / 86_400_000
  if (days <= 3) return 'bg-amber-400'
  return 'bg-green-400'
}

export function buildQueueColDefs(params: {
  userId: string | undefined
  userRole: string | undefined
  onChangeStatus: (vars: { itemId: string; status: RequestItemStatus }) => void
  onChangeProcState: (vars: { itemId: string; state: RequestItemProcurementState }) => void
  /** Снятие claim производителя (кнопка в тултипе метки) — только ROS/ADMIN. */
  onReleaseManufacturerClaim?: (itemId: string, holderName: string) => void
}): ColDef<QueueRow>[] {
  const { userId, userRole, onChangeStatus, onChangeProcState, onReleaseManufacturerClaim } = params
  const isProcurementActor = userRole === 'PROCUREMENT' || userRole === 'ROS'
  const isTeam = userRole === 'ROS'
  const canReleaseClaim = userRole === 'ROS' || userRole === 'ADMIN'

  // Колонка чекбоксов добавляется AG Grid автоматически при
  // rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
  // (v35: старый checkboxSelection в colDef помечен deprecated → v36 удалит).
  return [
    {
      field: 'categoryLabel',
      headerName: 'Кат.',
      minWidth: 70,
      width: 80,
      cellRenderer: (p: { data: QueueRow }) => (
        <CategoryBadge category={p.data._raw.clientCategory} />
      ),
    },
    {
      field: 'clientCode',
      headerName: 'Код клиента',
      minWidth: 110,
      cellClass: 'font-mono text-xs text-gray-500',
    },
    {
      field: 'requestNumber',
      headerName: 'Заявка',
      minWidth: 110,
      cellRenderer: (p: { data: QueueRow }) => (
        <Link
          to={`/requests/${p.data._raw.requestId}`}
          className="text-brand-600 hover:text-brand-700 text-xs"
        >
          {p.data.requestNumber}
        </Link>
      ),
    },
    {
      field: 'partNumber',
      headerName: 'Артикул',
      minWidth: 140,
      cellRenderer: (p: { data: QueueRow }) => (
        <span>
          <Link
            to={`/request-items/${p.data._raw.requestItemId}`}
            className="font-mono text-brand-600 hover:text-brand-700 font-medium"
          >
            {p.data.partNumber}
          </Link>
          {p.data._raw.hasProcurementResult && (
            <span className="ml-1 text-green-600" title="Есть квоты">
              ✓
            </span>
          )}
          {p.data._raw.hasComments && (
            <span className="ml-0.5 text-gray-400" title="Есть комментарии">
              💬
            </span>
          )}
          {p.data._raw.manufacturerClaim && (
            // «Занято производителем»: прямой запрос уже отправлен коллегой —
            // координация, чтобы второй закупщик не дёргал того же производителя.
            // РОЗ/ADMIN кликом снимают claim (закупщик уволился — позиция не виснет).
            <span
              onClick={
                canReleaseClaim && onReleaseManufacturerClaim
                  ? (e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onReleaseManufacturerClaim(
                        p.data._raw.requestItemId,
                        p.data._raw.manufacturerClaim?.byName ?? '—',
                      )
                    }
                  : undefined
              }
              title={`Занято производителем: ${p.data._raw.manufacturerClaim.byRole} ${p.data._raw.manufacturerClaim.byName} · ${new Date(p.data._raw.manufacturerClaim.at).toLocaleString('ru-RU')}${canReleaseClaim ? ' — нажмите, чтобы снять' : ''}`}
              className={`ml-1 inline-block px-1 py-0.5 rounded text-[10px] leading-none bg-amber-100 text-amber-700 align-middle ${canReleaseClaim ? 'cursor-pointer hover:bg-amber-200' : ''}`}
            >
              Произв.
            </span>
          )}
        </span>
      ),
    },
    { field: 'brand', headerName: 'Бренд', minWidth: 110 },
    {
      field: 'noteForProcurement',
      headerName: 'Заметка',
      minWidth: 180,
      wrapText: true,
      autoHeight: true,
      cellClass: 'text-xs text-amber-700 whitespace-pre-wrap leading-snug',
    },
    { field: 'qtyLabel', headerName: 'Кол-во', minWidth: 100 },
    {
      field: 'priorityLabel',
      headerName: 'Приор.',
      minWidth: 100,
      cellRenderer: (p: { data: QueueRow }) => <PriorityBadge priority={p.data._raw.priority} />,
    },
    {
      field: 'deadlineLabel',
      headerName: 'Дедлайн',
      minWidth: 110,
      comparator: (_a: string, _b: string, nodeA, nodeB) => {
        const da = (nodeA?.data as QueueRow | undefined)?._raw.requestDeadline
        const db = (nodeB?.data as QueueRow | undefined)?._raw.requestDeadline
        const ta = da ? new Date(da).getTime() : 0
        const tb = db ? new Date(db).getTime() : 0
        return ta - tb
      },
      cellRenderer: (p: { data: QueueRow }) => {
        const { requestDeadline, isOverdue } = p.data._raw
        if (!requestDeadline) return <span className="text-gray-400">—</span>
        const dot = deadlineDot(requestDeadline, isOverdue)
        const cls = deadlineClass(requestDeadline, isOverdue)
        return (
          <span className={`flex items-center gap-1 ${cls}`}>
            {dot && <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />}
            {p.data.deadlineLabel}
          </span>
        )
      },
    },
    {
      field: 'statusLabel',
      headerName: 'Статус',
      minWidth: 170,
      cellRenderer: (p: { data: QueueRow }) => {
        const item = p.data._raw

        // Для PROCUREMENT / ROS — показываем ЗАКУПЩИЦКИЙ per-user state и
        // дропдаун ручных переходов (WAITING / NEEDS_INFO / NO_RESPONSE / REFUSED).
        // Глобальный item.status закупщику не показываем — это менеджерский цикл.
        if (isProcurementActor) {
          const procState = (item.procurementState ?? 'NEW') as RequestItemProcurementState
          // QUOTED — финальный, только бейдж, без дропдауна.
          if (procState === 'QUOTED') {
            return <ProcItemStatusBadge state="QUOTED" team={isTeam} />
          }
          // Для ROS (командный scope) дропдаун не показываем — РОЗ видит агрегат,
          // а свои ручные переходы ставит на странице позиции.
          if (isTeam) {
            return <ProcItemStatusBadge state={procState} team />
          }
          return (
            <select
              value={procState}
              onChange={(e) =>
                onChangeProcState({
                  itemId: item.requestItemId,
                  state: e.target.value as RequestItemProcurementState,
                })
              }
              className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer"
            >
              <option value={procState}>{PROCUREMENT_STATE_LABELS[procState]}</option>
              {PROCUREMENT_STATE_MANUAL.filter((s) => s !== procState).map((s) => (
                <option key={s} value={s}>
                  {PROCUREMENT_STATE_LABELS[s]}
                </option>
              ))}
            </select>
          )
        }

        // Прочие роли (в очереди — только ГД) — глобальный item.status + старые
        // quick-переходы.
        const quickOptions = quickTransitionsFor(item.itemStatus as RequestItemStatus)
        if (quickOptions.length === 0) return <ItemStatusBadge status={item.itemStatus} />
        return (
          <select
            value={item.itemStatus}
            onChange={(e) =>
              onChangeStatus({
                itemId: item.requestItemId,
                status: e.target.value as RequestItemStatus,
              })
            }
            className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer"
          >
            <option value={item.itemStatus}>{itemStatusLabel(item.itemStatus)}</option>
            {quickOptions.map((s) => (
              <option key={s} value={s}>
                {itemStatusLabel(s)}
              </option>
            ))}
          </select>
        )
      },
    },
    {
      field: 'assigneeLabel',
      headerName: 'Назначен',
      minWidth: 110,
      cellRenderer: (p: { data: QueueRow }) => {
        const item = p.data._raw
        if (item.assignedToId === userId)
          return <span className="text-brand-600 font-medium">Я</span>
        if (item.assignedToId) return <span className="text-gray-400">назначен</span>
        return <span className="text-gray-300">—</span>
      },
    },
    {
      field: 'managerLabel',
      headerName: 'Менеджер',
      minWidth: 130,
      cellClass: 'text-xs text-gray-700',
    },
  ]
}
